import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Plus, Search, Edit, Trash2, Package } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Products() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sort: 'newest' };
      if (search) params.search = search;
      // Admin needs to see every product by default. Empty filter means
      // "Todos" → request status=all so backend skips the active-only filter
      // that protects the public store.
      params.status = statusFilter || 'all';
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`¿Eliminar producto "${name}"?`)) return;
    try {
      await axios.delete(`${API}/products/${id}`, { headers });
      toast.success('Producto eliminado');
      fetch();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Optimistic toggle: flip status locally first, then call backend; revert on failure.
  const handleToggleStatus = async (product) => {
    const newStatus = product.status === 'active' ? 'inactive' : 'active';
    setProducts(curr => curr.map(p => p.id === product.id ? { ...p, status: newStatus, _statusUpdating: true } : p));
    try {
      await axios.patch(`${API}/products/${product.id}/status`, { status: newStatus }, { headers });
      setProducts(curr => curr.map(p => p.id === product.id ? { ...p, _statusUpdating: false } : p));
      toast.success(newStatus === 'active'
        ? `"${product.name}" se activó y volverá a ser visible en la tienda`
        : `"${product.name}" se ocultó de la tienda`);
    } catch (err) {
      // Revert on failure
      setProducts(curr => curr.map(p => p.id === product.id ? { ...p, status: product.status, _statusUpdating: false } : p));
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el estado');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-semibold">Productos</h1>
        <Link to="/admin/productos/nuevo">
          <Button className="gap-2" data-testid="admin-products-create-button"><Plus className="h-4 w-4" /> Nuevo Producto</Button>
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre o SKU..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="inactive">Inactivos</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground self-center">{total} productos</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/30">
                  <th className="px-3 py-2 text-left font-medium">Producto</th>
                  <th className="px-3 py-2 text-left font-medium">SKU</th>
                  <th className="px-3 py-2 text-left font-medium">Marca</th>
                  <th className="px-3 py-2 text-right font-medium">Precio</th>
                  <th className="px-3 py-2 text-right font-medium">Stock</th>
                  <th className="px-3 py-2 text-center font-medium">Estado</th>
                  <th className="px-3 py-2 text-center font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin productos</td></tr>
                ) : products.map(p => (
                  <tr key={p.id} className="border-b hover:bg-secondary/20 transition-colors duration-150">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-secondary/50 rounded flex items-center justify-center flex-shrink-0">
                          {p.images?.[0] ? <img src={`${process.env.REACT_APP_BACKEND_URL}${p.images[0].url}`} alt="" className="w-full h-full object-cover rounded" /> : <Package className="h-4 w-4 text-muted-foreground/30" />}
                        </div>
                        <span className="font-medium truncate max-w-[200px]">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
                    <td className="px-3 py-2">{p.brand}</td>
                    <td className="px-3 py-2 text-right tabular-nums">${p.price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right">
                      <Badge variant={p.stock === 0 ? 'destructive' : p.stock <= 5 ? 'outline' : 'secondary'}>{p.stock}</Badge>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-2" data-testid={`admin-product-status-toggle-${p.id}`}>
                        <Switch
                          checked={p.status === 'active'}
                          disabled={p._statusUpdating}
                          onCheckedChange={() => handleToggleStatus(p)}
                          aria-label={p.status === 'active' ? `Desactivar ${p.name}` : `Activar ${p.name}`}
                          data-testid={`admin-product-status-switch-${p.id}`}
                        />
                        <Badge
                          variant={p.status === 'active' ? 'secondary' : 'outline'}
                          className={p.status === 'active' ? '' : 'text-muted-foreground'}
                        >
                          {p.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex justify-center gap-1">
                        <Link to={`/admin/productos/${p.id}`}><Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button></Link>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(p.id, p.name)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="flex items-center text-sm text-muted-foreground">Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
